/**
 * A board card: the review card, read-only and at a column's width.
 *
 * Same parts as the Review queue's `RecordCard` — the record's kind chip, its
 * title, the tray of property pills drawn with the phone's marks
 * (`fieldsFor` + `propertyMarks`), its words, its photos — so a record looks
 * like itself on both screens. Two departures, both for the board: the
 * assignee is the initials in the top-right corner rather than a pill among
 * the others, and the status pill is gone because the column already says it.
 * Unset properties are left out instead of reading "Not set" on every card.
 */
import type { CSSProperties, ReactNode } from "react";
import type { ConsoleRow } from "../../api";
import { CHIP_FONT, FIELD_NAME_STYLE, Mark, fieldsNeedingTheirName, markFor, type RecordChipFieldType } from "../../../../../backend/src/frontend/components/record/propertyMarks";
import { DsIcon } from "../../components/DsIcon";
import { PROCORE_MARK_URL } from "../../assets/procoreMark";
import { ADR, C, DS, FONT_MONO, FONT_TEXT, RD, RT } from "../../tokens";
import { fieldsFor, type ReviewField } from "../ReviewQueue";
import { PHONE_KIND_INK, PHONE_MARK_TOKENS, PhoneKindGlyph, phoneKindSoft, phoneMarkInputs, phoneMarkValue, procoreKindOf } from "../ReviewCardParts";
import type { RecordFacts } from "./recordModel";
import { NoAvatar, PersonAvatar } from "./parts";

/** Properties the board draws elsewhere, or not at all. */
const OFF_TRAY = new Set(["status", "assignee"]);

export function BoardCard({ f, issues, showSite, siteName, photos, link, lifted = false }: {
  f: RecordFacts;
  /** An Autodesk issue: the chip says Issue, whatever the row's kind. */
  issues: boolean;
  showSite: boolean;
  siteName: (jobSiteId: string) => string;
  photos: ReactNode;
  link: Record<string, unknown>;
  /** Being dragged: the card stays in place, faded, until it is dropped. */
  lifted?: boolean;
}) {
  const item = f.row;
  const kind = procoreKindOf(item);
  const phoneKind = issues ? "observation" : kind.kind;
  const ink = phoneKind ? PHONE_KIND_INK[phoneKind] : ADR.blue;
  const soft = phoneKind ? phoneKindSoft(phoneKind) : ADR.blueSoft;
  const fields = fieldsFor(item);
  const prose = fields.find((field) => field.multiline && field.rawValue.trim());
  const pills = fields.filter((field) => !field.multiline && !OFF_TRAY.has(field.key) && field.rawValue.trim());
  const named = fieldsNeedingTheirName(
    pills.map((field) => ({ key: field.key, label: field.label, value: field.rawValue, glyph: phoneMarkInputs(field).glyph, editable: !field.calculated })),
    Object.fromEntries(pills.map((field) => [field.key, phoneMarkInputs(field).type ?? "text"])) as Record<string, RecordChipFieldType>,
  );
  const now = Date.now();

  return (
    <div
      className="rt-card"
      data-testid="records-row"
      data-item-id={item.id}
      {...link}
      style={{ position: "relative", background: ADR.paper, border: "1px solid rgba(20,17,12,.08)", borderRadius: 16, boxShadow: "0 4px 14px rgba(20,17,12,.05)", padding: "14px 14px 12px", cursor: link.draggable ? "grab" : "pointer", outline: "none", opacity: lifted ? 0.4 : 1 }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span data-testid="board-kind-chip" style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 24, padding: "0 9px 0 8px", borderRadius: 999, background: soft, color: ink, flex: "none" }}>
          {phoneKind && <PhoneKindGlyph kind={phoneKind} size={12} />}
          <span style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, letterSpacing: "1.3px", textTransform: "uppercase", whiteSpace: "nowrap" }}>{issues ? "Issue" : kind.chip}</span>
        </span>
        {f.assignee ? (
          <span title={f.company ? `${f.assignee} · ${f.company}` : f.assignee} data-testid="board-assignee">
            <PersonAvatar name={f.assignee} size={24} />
          </span>
        ) : (
          <span title="Unassigned"><NoAvatar size={24} /></span>
        )}
      </div>

      <div data-testid="row-title" style={{ marginTop: 10, fontFamily: FONT_TEXT, fontSize: 16, fontWeight: 800, letterSpacing: "-0.4px", lineHeight: 1.2, color: C.ink, overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {item.title}
      </div>

      {pills.length > 0 && (
        <div data-testid="board-field-tray" style={{ ...PHONE_MARK_TOKENS, display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10, padding: 6, borderRadius: 12, background: ADR.tray }}>
          {pills.map((field) => (
            <BoardPill key={field.key} field={field} nameIt={named.has(field.key)} overdue={field.key === "dueDate" && f.overdue} now={now} />
          ))}
        </div>
      )}

      {prose && (
        <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.45, color: DS.body2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {prose.rawValue}
        </div>
      )}

      {photos}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 12 }}>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: ADR.label }}>
          {[f.number ? `#${f.number}` : null, showSite ? siteName(item.jobSiteId) : null, dayStamp(f.created)].filter(Boolean).join(" · ")}
        </span>
        {f.sync === "synced" && (
          <span title="Synced to Procore" style={{ flex: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <DsIcon name="check" size={10} color={DS.green} />
            <img src={PROCORE_MARK_URL} alt="" style={{ width: 11, height: 11, borderRadius: 2, display: "block" }} />
          </span>
        )}
        {f.sync === "pending" && <span style={{ flex: "none", fontSize: 11.5, fontWeight: 600, color: RT.status.working }}>Syncing</span>}
        {f.sync === "failed" && <span title={item.procoreSync?.lastError} style={{ flex: "none", fontSize: 11.5, fontWeight: 600, color: DS.red }}>Sync failed</span>}
      </div>
    </div>
  );
}

/** One property, as the review card's pill draws it — the phone's mark, the
 *  field's name where the mark alone is ambiguous, the value — but read-only. */
function BoardPill({ field, nameIt, overdue, now }: { field: ReviewField; nameIt: boolean; overdue: boolean; now: number }) {
  const { glyph, type } = phoneMarkInputs(field);
  const raw = field.rawValue;
  const shown = type === "date" ? shortDate(raw) ?? field.value : field.value;
  return (
    <span
      data-testid="board-field"
      data-field={field.key}
      title={`${field.label}: ${field.value}`}
      style={PILL}
    >
      <Mark mark={markFor(glyph, type, field.label)} value={phoneMarkValue(field, raw, shown)} fieldKey={field.key} dateValue={type === "date" ? raw.slice(0, 10) || null : undefined} now={now} />
      {nameIt && <span style={{ ...FIELD_NAME_STYLE, fontSize: 9.5 }}>{field.label}</span>}
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600, color: overdue ? C.red : C.ink }}>{shown}</span>
    </span>
  );
}

const PILL: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  height: 28,
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "0 10px 0 8px",
  borderRadius: 999,
  border: "1px solid rgba(20,17,12,.08)",
  background: ADR.card,
  fontFamily: CHIP_FONT,
  fontSize: 12,
  letterSpacing: "-0.01em",
  color: RD.ink,
  whiteSpace: "nowrap",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The card's created day, `Oct 21` — the year only when it is not this one. */
function dayStamp(date: Date): string {
  const day = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
  return date.getFullYear() === new Date().getFullYear() ? day : `${day}, ${date.getFullYear()}`;
}

/** `2026-10-20` → `Oct 20`, read as the calendar day it names. */
function shortDate(value: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return undefined;
  return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}`;
}
